import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { debounce } from "vue-debounce";
import { useApiClient } from "@/api/client";
import { ApiError, ok, Result } from "@/api/base";
import { budgetAllocationIssue } from "@/modules/base_price/utils/budgetAllocation";
import {
	ShareBudgetMismatch,
	shareBudgetMismatch,
} from "@/modules/base_price/utils/shareBudgetMismatch";
import { hierarchyLevels } from "@/modules/base_price/utils/hierarchyLevels";
import BasePricePlanDTO = Modules.BasePrice.Data.BasePricePlanDTO;
import BasePriceImpressionsStatusDTO = Modules.BasePrice.Data.BasePriceImpressionsStatusDTO;
import BasePricePlanMinimalDTO = Modules.BasePrice.Data.BasePricePlanMinimalDTO;
import BasePriceBookDTO = Modules.BasePrice.Data.Books.BasePriceBookDTO;
import ReservationBookDTO = Modules.BasePrice.Data.Books.ReservationBookDTO;
import ResolvedPlanDTO = Modules.BasePrice.Data.Resolved.ResolvedPlanDTO;
import ResolvedBookDTO = Modules.BasePrice.Data.Resolved.ResolvedBookDTO;
import ResolvedNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedNodeDTO;
import ResolvedAssetDTO = Modules.BasePrice.Data.Resolved.ResolvedAssetDTO;
import BasePriceOverrideDTO = Modules.BasePrice.Data.Overrides.BasePriceOverrideDTO;
import BasePriceOverrideType = Modules.BasePrice.Data.Overrides.BasePriceOverrideType;
import SplitOverrideDTO = Modules.BasePrice.Data.Overrides.SplitOverrideDTO;
import FixedBudgetOverrideDTO = Modules.BasePrice.Data.Overrides.FixedBudgetOverrideDTO;
import AssetFixedBudgetOverrideDTO = Modules.BasePrice.Data.Overrides.AssetFixedBudgetOverrideDTO;
import SeasonOverrideDTO = Modules.BasePrice.Data.Overrides.SeasonOverrideDTO;
import FillOverrideDTO = Modules.BasePrice.Data.Overrides.FillOverrideDTO;
import ManualCpmOverrideDTO = Modules.BasePrice.Data.Overrides.ManualCpmOverrideDTO;
import MonthBudgetOverrideDTO = Modules.BasePrice.Data.Overrides.MonthBudgetOverrideDTO;
import CpmBoundsOverrideDTO = Modules.BasePrice.Data.Overrides.CpmBoundsOverrideDTO;
import ImpressionShareOverrideDTO = Modules.BasePrice.Data.Overrides.ImpressionShareOverrideDTO;
import DaySeasonOverrideDTO = Modules.BasePrice.Data.Overrides.DaySeasonOverrideDTO;
import DayBudgetOverrideDTO = Modules.BasePrice.Data.Overrides.DayBudgetOverrideDTO;
import DayFillOverrideDTO = Modules.BasePrice.Data.Overrides.DayFillOverrideDTO;
import DayManualCpmOverrideDTO = Modules.BasePrice.Data.Overrides.DayManualCpmOverrideDTO;
import BasePriceDailyImpressionsDTO = Modules.BasePrice.Data.BasePriceDailyImpressionsDTO;

/**
 * A book as it actually arrives over the wire: the abstract base plus the
 * reservation-only fields, since the generated TS type for a polymorphic
 * DTO collection can't statically narrow by `type` the way PHP's
 * BasePriceBookType::getImplementationClass() does.
 */
export type BasePriceBook = BasePriceBookDTO & Partial<ReservationBookDTO>;

/** Same story as BasePriceBook, but for the five override kinds. */
export type BasePriceOverride = BasePriceOverrideDTO &
	Partial<SplitOverrideDTO> &
	Partial<FixedBudgetOverrideDTO> &
	Partial<AssetFixedBudgetOverrideDTO> &
	Partial<SeasonOverrideDTO> &
	Partial<FillOverrideDTO> &
	Partial<ManualCpmOverrideDTO> &
	Partial<MonthBudgetOverrideDTO> &
	Partial<CpmBoundsOverrideDTO> &
	Partial<ImpressionShareOverrideDTO> &
	Partial<DaySeasonOverrideDTO> &
	Partial<DayBudgetOverrideDTO> &
	Partial<DayFillOverrideDTO> &
	Partial<DayManualCpmOverrideDTO>;

/**
 * Whatever the editor is currently pointed at — a hierarchy node, or one
 * screen (Asset) attached to one. The two are priced identically by the
 * resolver (see ResolvedAssetDTO), so the Seasonality and Fill & CPM tabs
 * work against this shape rather than a ResolvedNodeDTO and never need to
 * know which they are editing. Budget split stays node-only: how a budget
 * divides among children is not a question a single screen answers.
 */
/**
 * The values a target reverts to when its own override is cleared. Only the
 * fields the three override tabs seed from, because that is the only thing a
 * fallback is ever read for — which is what lets a resolved node and the
 * book's built-in defaults stand in for each other here.
 */
export type PlanInheritedValues = {
	monthly_season_shares: number[];
	monthly_season_locks: boolean[];
	monthly_fill_rates: number[];
	monthly_manual_cpm: ResolvedNodeDTO["monthly_manual_cpm"];
	monthly_impression_share: number[];
	cpm_min: number | null;
	cpm_max: number | null;
};

export type PlanEditTarget = {
	kind: "node" | "asset";
	/** hierarchy_node_id for a node, asset_id for a screen — separate id spaces, never mixed. */
	id: number;
	name: string;
	kindLabel: string;
	/** A screen's key (Asset::$name_clean); null for a node, which has none. */
	nameClean: string | null;

	/**
	 * What the target was handed (a node's allotted_budget), not what its
	 * rows happen to add up to — a node fixed at €10K whose children only
	 * take €5K still shows €10K, with revenue against it at 50%.
	 */
	budget: number;
	cpm: number;
	gross_impressions: number;
	sellable_impressions: number;
	revenue: number;

	monthly_budget: number[];
	monthly_gross_impressions: number[];
	monthly_sellable_impressions: number[];
	monthly_revenue: number[];

	/** The shape actually in effect, with any month pins already folded in. */
	monthly_season_shares: number[];
	/** The weights as authored, before pins — what the editor restores from. */
	monthly_season_weights: number[];
	monthly_season_locks: boolean[];
	season_own: boolean;
	monthly_budget_pins: ResolvedNodeDTO["monthly_budget_pins"];
	month_budget_own: boolean;
	season_fully_allocated: boolean;
	/** Does this exact target carry locked or pinned days of its own? */
	days_own: boolean;
	/** Do its pinned days fit the months they come out of? See DayPins. */
	days_fully_allocated: boolean;
	/** Does this exact target carry day-level fill or CPM of its own? */
	day_pricing_own: boolean;
	monthly_fill_rates: number[];
	fill_own: boolean;
	monthly_manual_cpm: ResolvedNodeDTO["monthly_manual_cpm"];
	manual_cpm_own: boolean;
	monthly_impression_share: number[];
	impression_share_own: boolean;
	cpm_min: number | null;
	cpm_max: number | null;
	cpm_bounds_own: boolean;
	cpm_within_bounds: boolean;
	monthly_cpm_clamped: boolean[];

	/**
	 * What this target falls back to when its own override is cleared: a
	 * node's parent, for a screen the node it hangs off (a screen is the end
	 * of an inheritance chain, so its fallback is always exactly one hop),
	 * and at the root the book's own built-in defaults.
	 *
	 * The root having no parent never meant it had nothing to revert to — an
	 * override cleared there falls back to the book default, exactly as it
	 * does anywhere else. Null only when the book hasn't resolved yet.
	 */
	inheritsFrom: PlanInheritedValues | null;

	/**
	 * Where the seasonality currently in effect was actually set, when it
	 * isn't this target's own — a node can inherit from any ancestor, a
	 * screen only ever from the node carrying it. Null when the target owns
	 * its shape, and when nothing above it overrides one either.
	 */
	seasonSource: ResolvedNodeDTO | null;

	/** Rows for the "per level below" breakdown — always empty for a screen. */
	children: ResolvedNodeDTO[];
	assets: ResolvedAssetDTO[];
};

/**
 * One thing wrong with the plan, and enough to go and fix it. Collected
 * across every book rather than per selection, so the count in the header
 * answers "is this plan sound?" without opening anything.
 */
export type PlanIssue = {
	id: string;
	bookUuid: string;
	bookName: string;
	nodeId: number;
	assetId: number | null;
	targetName: string;
	/** The tab that can fix it. */
	tab: string;
	tabLabel: string;
	title: string;
	detail: string;
};

const randomUuid = (): string =>
	crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;

/**
 * Central store for one base price plan being edited. This is the first
 * slice of what will grow into the full editor state (tree selection, the
 * seasonality/fill/CPM drafts, recommendations, ...) as components are
 * added one by one — see the BasePrice project memory for the build order.
 */
export const useBasePriceStore = defineStore("base-price", () => {
	const plan = ref<BasePricePlanDTO | null>(null);
	const loading = ref(false);
	const resolving = ref(false);
	const resolved = ref<ResolvedPlanDTO | null>(null);
	const selectedBookUuid = ref<string | null>(null);

	/**
	 * Which tab is open in PlanNodeDetailPanel — lives here, not as local
	 * state in that component, so PlanHierarchyRow.vue (a sibling under
	 * BasePricePlanEditor.vue, not a descendant) can read it too: the
	 * hierarchy sidebar's orange override marker is contextual to whichever
	 * tab is active (a node's split/fixed-budget override marks it while
	 * viewing Budget split, its season override marks it while viewing
	 * Seasonality, etc.) rather than always showing the Budget split state
	 * regardless of what you're actually looking at.
	 */
	const activeDetailTab = ref("split");

	/**
	 * The working copy of every override, seeded from the committed plan and
	 * mutated locally as the user edits (e.g. a budget-share input). Not
	 * persisted anywhere yet — but sent in full on every preview resolve, so
	 * a pending edit here is never silently dropped by an unrelated change
	 * (e.g. editing the target) the way a target-only preview would.
	 */
	const draftOverrides = ref<BasePriceOverride[]>([]);

	/**
	 * The working copy of the plan's books, seeded from the committed plan and
	 * edited in the Budget books dialog. Sent in full on every preview resolve
	 * alongside the overrides, for the same reason: a carve-out change and a
	 * pending split edit can be in flight at once and neither should discard
	 * the other. Everything that reads `books` reads this, so the header cards
	 * and every resolved figure follow an edit the moment it is applied.
	 */
	const draftBooks = ref<BasePriceBook[]>([]);

	// Laravel serializes a polymorphic collection as a JSON array when its
	// keys are consecutive, but the generated TS type also allows a keyed
	// object (`{ [key: number]: T }`) for the general case — normalize both
	// shapes to a plain array wherever one of these collections is read.
	const books = computed<BasePriceBook[]>(() => draftBooks.value);

	/** The plan's books as committed, normalized — what draftBooks is seeded from and what Cancel reverts to. */
	const committedBooks = (): BasePriceBook[] =>
		plan.value?.books ? Object.values(plan.value.books) : [];

	const resolvedBooks = computed<ResolvedBookDTO[]>(() =>
		resolved.value?.books ? Object.values(resolved.value.books) : []
	);

	const resolvedBook = (book: BasePriceBook): ResolvedBookDTO | null =>
		resolvedBooks.value.find((b) => b.book_uuid === book.uuid) ?? null;

	/**
	 * True if any node in any book hasn't assigned its entire received
	 * budget to its children (see ResolvedNodeDTO.budget_fully_allocated) —
	 * checked across every book, not just the one currently selected, since
	 * the hierarchy sidebar's red dot needs to be visible for a node
	 * regardless of which book/node you're currently looking at. No save
	 * endpoint exists yet, but this is the gate a future one should check
	 * before persisting: don't save while this is true.
	 */
	const hasAllocationErrors = computed(() =>
		resolvedBooks.value.some((book) =>
			(book.nodes ? Object.values(book.nodes) : []).some(
				(node) =>
					!node.budget_fully_allocated ||
					!node.season_fully_allocated ||
					!node.days_fully_allocated ||
					!node.cpm_within_bounds ||
					assetsOf(node).some(
						(asset) =>
							!asset.season_fully_allocated ||
							!asset.days_fully_allocated ||
							!asset.cpm_within_bounds ||
							overReservedAssetIds.value.has(asset.asset_id)
					)
			)
		) || shareBudgetMismatches.value.size > 0
	);

	/**
	 * Screens where the enabled reservation books together reserve more than
	 * all of the inventory in some month — two books promising the same
	 * impressions. Open inventory simply floors at zero there, so without this
	 * it is invisible.
	 *
	 * Checked per screen, not per node: a node's share is only what it passes
	 * down, while the reservation itself happens against a screen's own gross.
	 * Any collision an inherited node share causes therefore shows up on the
	 * screens beneath it, which is also where it has to be fixed.
	 *
	 * The Impression share tab caps what can be typed, but a cap only governs
	 * the field being typed in — setting a share on a parent, or re-enabling a
	 * book, reaches targets nobody is looking at.
	 */
	const overReservedAssetIds = computed<Set<number>>(() => {
		const monthlyTotals = new Map<number, number[]>();

		books.value
			.filter((book) => book.type === "reservation" && book.is_enabled)
			.forEach((book) => {
				const resolved = resolvedBook(book);
				if (!resolved) return;

				(resolved.nodes ? Object.values(resolved.nodes) : []).forEach((node) =>
					assetsOf(node).forEach((asset) => {
						const totals =
							monthlyTotals.get(asset.asset_id) ?? Array(12).fill(0);

						asset.monthly_impression_share.forEach(
							(share, i) => (totals[i] += share)
						);
						monthlyTotals.set(asset.asset_id, totals);
					})
				);
			});

		const over = new Set<number>();

		monthlyTotals.forEach((totals, assetId) => {
			// A hair over 100 is rounding, not a double booking.
			if (totals.some((total) => total > 100.05)) over.add(assetId);
		});

		return over;
	});

	/**
	 * Every place an enabled reservation book's impression share and budget
	 * contradict each other — see shareBudgetMismatch() — keyed by
	 * `bookUuid:node:id` or `bookUuid:asset:id`. Per book: a target can
	 * reserve nothing in one book and rightly carry budget in another.
	 */
	const shareBudgetMismatches = computed<Map<string, ShareBudgetMismatch>>(() => {
		const found = new Map<string, ShareBudgetMismatch>();

		books.value
			.filter((book) => book.type === "reservation" && book.is_enabled)
			.forEach((book) => {
				const resolved = resolvedBook(book);
				if (!resolved) return;

				(resolved.nodes ? Object.values(resolved.nodes) : []).forEach((node) => {
					const nodeIssue = shareBudgetMismatch({
						// What the node was handed: a 0% split is 0 here even when
						// a child's fixed amount makes it resolve to more.
						budget: node.allotted_budget,
						gross_impressions: node.gross_impressions,
						monthly_impression_share: node.monthly_impression_share,
						impression_share_own: node.impression_share_own,
						budget_own: node.split_own || node.fixed_budget_own,
					});
					if (nodeIssue) found.set(`${book.uuid}:node:${node.hierarchy_node_id}`, nodeIssue);

					assetsOf(node).forEach((asset) => {
						const assetIssue = shareBudgetMismatch({
							budget: asset.budget,
							gross_impressions: asset.gross_impressions,
							monthly_impression_share: asset.monthly_impression_share,
							impression_share_own: asset.impression_share_own,
							budget_own: asset.fixed_budget_own,
						});
						if (assetIssue) found.set(`${book.uuid}:asset:${asset.asset_id}`, assetIssue);
					});
				});
			});

		return found;
	});

	/** The share/budget contradiction set on this node or screen in the selected book, if any. */
	const shareBudgetIssue = (kind: "node" | "asset", id: number): ShareBudgetMismatch | null => {
		const book = selectedBook.value;

		return book ? shareBudgetMismatches.value.get(`${book.uuid}:${kind}:${id}`) ?? null : null;
	};

	/** Does this node carry a screen that more than one book has promised away? */
	const nodeOverReserved = (nodeId: number): boolean => {
		const node = nodeById(nodeId);

		return node
			? assetsOf(node).some((asset) =>
					overReservedAssetIds.value.has(asset.asset_id)
				)
			: false;
	};

	/**
	 * Every configuration problem in the plan, in one list. These are the same
	 * conditions the hierarchy marks with a red dot — deliberately not
	 * "revenue is off budget", which a hand-set price or a price bound is
	 * *supposed* to cause.
	 */
	const planIssues = computed<PlanIssue[]>(() => {
		const issues: PlanIssue[] = [];

		/** Where each screen lives, so an over-reservation can be navigated to. */
		const screenHome = new Map<number, { nodeId: number; name: string }>();

		books.value.forEach((book) => {
			const resolved = resolvedBook(book);
			if (!resolved) return;

			const priceTab =
				book.type === "open_inventory" ? "fill_cpm" : "cpm_per_month";
			const priceLabel =
				book.type === "open_inventory" ? "CPM & Fill" : "CPM per month";

			const add = (
				target: { nodeId: number; assetId: number | null; name: string },
				tab: string,
				tabLabel: string,
				title: string,
				detail: string
			) =>
				issues.push({
					id: `${book.uuid}:${target.assetId ?? target.nodeId}:${title}`,
					bookUuid: book.uuid,
					bookName: book.name,
					nodeId: target.nodeId,
					assetId: target.assetId,
					targetName: target.name,
					tab,
					tabLabel,
					title,
					detail,
				});

			const addShareBudgetIssue = (
				target: { nodeId: number; assetId: number | null; name: string },
				issue: ShareBudgetMismatch
			) =>
				add(
					target,
					"impression_share",
					"Impression share",
					issue === "budget_without_share"
						? "Budget, but nothing reserved"
						: "Impressions reserved, but no budget",
					issue === "budget_without_share"
						? "This reserves 0% of its impressions in every month, yet the book still gives it budget. Nothing is there to earn it, so the plan lands under its target."
						: "Its budget is set to nothing, yet it still reserves impressions — those are given away at a CPM of €0."
				);

			(resolved.nodes ? Object.values(resolved.nodes) : []).forEach((node) => {
				const asNode = {
					nodeId: node.hierarchy_node_id,
					assetId: null,
					name: node.name,
				};

				const budgetIssue = budgetAllocationIssue(node);
				if (budgetIssue) {
					add(
						asNode,
						"split",
						book.type === "open_inventory" ? "Budget split" : "Split & inventory",
						budgetIssue === "over" ? "Budget is over-allocated" : "Budget isn't fully allocated",
						budgetIssue === "over"
							? "The fixed and percentage shares set on its children add up to more than this level's budget."
							: "The fixed and percentage shares set on its children don't add up to this level's budget, and nothing is left free to absorb the difference."
					);
				}

				if (!node.season_fully_allocated) {
					add(
						asNode,
						"seasonality",
						"Monthly Budget Distribution",
						"Pinned months don't fit the budget",
						"The months pinned to exact amounts here ask for more than this level has — or every month is pinned and they don't total it exactly."
					);
				}

				if (!node.days_fully_allocated) {
					add(
						asNode,
						"seasonality",
						"Monthly Budget Distribution",
						"Pinned days don't fit their month",
						"The days pinned to exact amounts here ask for more than their month has — or every day of a month is pinned and they don't total it exactly."
					);
				}

				if (!node.cpm_within_bounds) {
					add(
						asNode,
						priceTab,
						priceLabel,
						"Hand-set CPM is outside the price bounds",
						"A CPM set by hand here falls outside the floor or ceiling in effect. The price is held at the bound regardless, so the two disagree about what was meant."
					);
				}

				const nodeShareIssue = shareBudgetMismatches.value.get(
					`${book.uuid}:node:${node.hierarchy_node_id}`
				);
				if (nodeShareIssue) addShareBudgetIssue(asNode, nodeShareIssue);

				assetsOf(node).forEach((asset) => {
					screenHome.set(asset.asset_id, {
						nodeId: node.hierarchy_node_id,
						name: asset.name,
					});

					const asAsset = {
						nodeId: node.hierarchy_node_id,
						assetId: asset.asset_id,
						name: asset.name,
					};

					if (!asset.season_fully_allocated) {
						add(
							asAsset,
							"seasonality",
							"Monthly Budget Distribution",
							"Pinned months don't fit the budget",
							"The months pinned to exact amounts on this screen ask for more than its budget."
						);
					}

					if (!asset.days_fully_allocated) {
						add(
							asAsset,
							"seasonality",
							"Monthly Budget Distribution",
							"Pinned days don't fit their month",
							"The days pinned to exact amounts on this screen ask for more than their month has."
						);
					}

					if (!asset.cpm_within_bounds) {
						add(
							asAsset,
							priceTab,
							priceLabel,
							"Hand-set CPM is outside the price bounds",
							"A CPM set by hand on this screen falls outside the floor or ceiling in effect."
						);
					}

					const assetShareIssue = shareBudgetMismatches.value.get(
						`${book.uuid}:asset:${asset.asset_id}`
					);
					if (assetShareIssue) addShareBudgetIssue(asAsset, assetShareIssue);
				});
			});
		});

		// Once per screen, not once per book: two books promising the same
		// inventory is one problem, not two.
		overReservedAssetIds.value.forEach((assetId) => {
			const home = screenHome.get(assetId);
			if (!home) return;

			issues.push({
				id: `over-reserved:${assetId}`,
				bookUuid: reservationBooks.value[0]?.uuid ?? "",
				bookName: "Reservation books",
				nodeId: home.nodeId,
				assetId,
				targetName: home.name,
				tab: "impression_share",
				tabLabel: "Impression share",
				title: "Reserved by more than one book beyond its inventory",
				detail:
					"The reservation books together reserve more of this screen than exists in at least one month. Open inventory is left with nothing there.",
			});
		});

		return issues;
	});

	const openInventoryBook = computed(
		() => books.value.find((book) => book.type === "open_inventory") ?? null
	);

	const reservationBooks = computed(() =>
		books.value.filter((book) => book.type === "reservation")
	);

	const selectedBook = computed(
		() =>
			books.value.find((book) => book.uuid === selectedBookUuid.value) ??
			openInventoryBook.value
	);

	const selectedNodeId = ref<number | null>(null);

	/**
	 * One screen (Asset) selected *within* selectedNode — a separate id space
	 * from selectedNodeId, since a screen is not a hierarchy node and can
	 * never be one. Selecting a different node or book clears it: a screen
	 * only exists in the context of the node it hangs off.
	 */
	const selectedAssetId = ref<number | null>(null);

	/** Every resolved node for the currently selected book, flat — the hierarchy sidebar nests these client-side by parent_id. */
	const selectedBookNodes = computed<ResolvedNodeDTO[]>(() => {
		const book = selectedBook.value;
		if (!book) return [];

		const resolvedForBook = resolvedBook(book);
		return resolvedForBook?.nodes ? Object.values(resolvedForBook.nodes) : [];
	});

	const nodeById = (id: number): ResolvedNodeDTO | null =>
		selectedBookNodes.value.find((node) => node.hierarchy_node_id === id) ??
		null;

	/**
	 * What the selected book falls back to when nothing overrides it anywhere:
	 * the shape and sell-through the resolver starts from, plus no manual CPM,
	 * no price bounds, and — for a reservation book — the flat share it was
	 * defined with.
	 *
	 * This is the root's fallback. A node's parent is the fallback everywhere
	 * else, but the root has no parent and still inherits: from the book. The
	 * resolver sends the first two (they are per book type, see
	 * BasePriceBookResolver's DEFAULT_* constants) rather than the editor
	 * keeping its own copy, which would drift the moment either side changed.
	 */
	const bookDefaults = computed<PlanInheritedValues | null>(() => {
		const book = selectedBook.value;
		if (!book) return null;

		const resolvedForBook = resolvedBook(book);
		if (!resolvedForBook) return null;

		return {
			monthly_season_shares: resolvedForBook.default_monthly_season_shares,
			// Defaults carry no locks and no hand-set prices: those only ever
			// exist because somebody set them on a specific target.
			monthly_season_locks: Array(12).fill(false) as boolean[],
			monthly_fill_rates: resolvedForBook.default_monthly_fill_rates,
			monthly_manual_cpm: Array(12).fill(null) as (number | null)[],
			// The book's own flat share, which is exactly what
			// ImpressionShareResolver starts from. Open inventory has no
			// impression share tab, so the zero is never read there.
			monthly_impression_share: Array(12).fill(
				book.impression_percentage ?? 0
			) as number[],
			cpm_min: null,
			cpm_max: null,
		};
	});

	const childrenOf = (id: number): ResolvedNodeDTO[] =>
		selectedBookNodes.value.filter((node) => node.parent_id === id);

	/** Root-to-parent path for a node, excluding the node itself — for the breadcrumb above the detail panel. */
	const ancestorsOf = (id: number): ResolvedNodeDTO[] => {
		const path: ResolvedNodeDTO[] = [];
		let current = nodeById(id);

		while (current && current.parent_id !== null) {
			const parent = nodeById(current.parent_id);
			if (!parent) break;
			path.unshift(parent);
			current = parent;
		}

		return path;
	};

	/**
	 * There's no explicit node "type" in the hierarchy model beyond
	 * root/branch/leaf — this is a display label only, derived from tree
	 * position, not a stored classification.
	 *
	 * Every hierarchy node is a group: an arbitrary grouping of screens,
	 * whether or not it has child groups of its own. Only an Asset is a
	 * screen, and it gets its label from the edit target, not from here. A
	 * bottom-level node used to be labelled "Screen" on the reasoning that it
	 * stood in for the screens beneath it — that was never quite true (a node
	 * usually carries several) and is plainly wrong now that a screen is a
	 * selectable target in its own right.
	 */
	const nodeKind = (node: ResolvedNodeDTO): string =>
		node.parent_id === null ? "Portfolio root" : "Group";

	const selectedNode = computed<ResolvedNodeDTO | null>(() =>
		selectedNodeId.value !== null ? nodeById(selectedNodeId.value) : null
	);

	/** A node's screens, normalized out of the keyed-object-or-array shape Laravel serializes a Collection to. */
	const assetsOf = (node: ResolvedNodeDTO | null): ResolvedAssetDTO[] =>
		node?.assets ? Object.values(node.assets) : [];

	/**
	 * Resolves to null the moment the selected node stops carrying that
	 * screen, so nothing downstream ever renders against a screen that is no
	 * longer in the resolved payload.
	 */
	const selectedAsset = computed<ResolvedAssetDTO | null>(() =>
		selectedAssetId.value === null
			? null
			: (assetsOf(selectedNode.value).find(
					(asset) => asset.asset_id === selectedAssetId.value
				) ?? null)
	);

	/**
	 * The node or screen the editor tabs act on. A screen wins when one is
	 * selected; everything else falls back to the node. Both carry the same
	 * monthly arrays and own-flags because the resolver prices them the same
	 * way — see ResolvedAssetDTO.
	 */
	const editTarget = computed<PlanEditTarget | null>(() => {
		const node = selectedNode.value;
		if (!node) return null;

		const asset = selectedAsset.value;

		if (asset) {
			return {
				kind: "asset",
				id: asset.asset_id,
				name: asset.name,
				kindLabel: "Screen",
				nameClean: asset.name_clean,
				budget: asset.budget,
				cpm: asset.cpm,
				gross_impressions: asset.gross_impressions,
				sellable_impressions: asset.sellable_impressions,
				revenue: asset.revenue,
				monthly_budget: asset.monthly_budget,
				monthly_gross_impressions: asset.monthly_gross_impressions,
				monthly_sellable_impressions: asset.monthly_sellable_impressions,
				monthly_revenue: asset.monthly_revenue,
				monthly_season_shares: asset.monthly_season_shares,
				monthly_season_weights: asset.monthly_season_weights,
				monthly_season_locks: asset.monthly_season_locks,
				monthly_budget_pins: asset.monthly_budget_pins,
				month_budget_own: asset.month_budget_own,
				season_fully_allocated: asset.season_fully_allocated,
				days_own: asset.days_own,
				days_fully_allocated: asset.days_fully_allocated,
				day_pricing_own: asset.day_pricing_own,
				season_own: asset.season_own,
				monthly_fill_rates: asset.monthly_fill_rates,
				fill_own: asset.fill_own,
				monthly_manual_cpm: asset.monthly_manual_cpm,
				manual_cpm_own: asset.manual_cpm_own,
				monthly_impression_share: asset.monthly_impression_share,
				impression_share_own: asset.impression_share_own,
				cpm_min: asset.cpm_min,
				cpm_max: asset.cpm_max,
				cpm_bounds_own: asset.cpm_bounds_own,
				cpm_within_bounds: asset.cpm_within_bounds,
				monthly_cpm_clamped: asset.monthly_cpm_clamped,
				// A screen's one and only fallback is the node carrying it.
				inheritsFrom: node,
				seasonSource: asset.season_own ? null : node,
				children: [],
				assets: [],
			};
		}

		return {
			kind: "node",
			id: node.hierarchy_node_id,
			name: node.name,
			kindLabel: nodeKind(node),
			nameClean: null,
			budget: node.allotted_budget,
			cpm: node.cpm,
			gross_impressions: node.gross_impressions,
			sellable_impressions: node.sellable_impressions,
			revenue: node.revenue,
			monthly_budget: node.monthly_budget,
			monthly_gross_impressions: node.monthly_gross_impressions,
			monthly_sellable_impressions: node.monthly_sellable_impressions,
			monthly_revenue: node.monthly_revenue,
			monthly_season_shares: node.monthly_season_shares,
			monthly_season_weights: node.monthly_season_weights,
			monthly_season_locks: node.monthly_season_locks,
			monthly_budget_pins: node.monthly_budget_pins,
			month_budget_own: node.month_budget_own,
			season_fully_allocated: node.season_fully_allocated,
			days_own: node.days_own,
			days_fully_allocated: node.days_fully_allocated,
			day_pricing_own: node.day_pricing_own,
			season_own: node.season_own,
			monthly_fill_rates: node.monthly_fill_rates,
			fill_own: node.fill_own,
			monthly_manual_cpm: node.monthly_manual_cpm,
			manual_cpm_own: node.manual_cpm_own,
			monthly_impression_share: node.monthly_impression_share,
			impression_share_own: node.impression_share_own,
			cpm_min: node.cpm_min,
			cpm_max: node.cpm_max,
			cpm_bounds_own: node.cpm_bounds_own,
			cpm_within_bounds: node.cpm_within_bounds,
			monthly_cpm_clamped: node.monthly_cpm_clamped,
			// The root falls back to the book's defaults rather than to nothing,
			// which is what lets it be reset like any other node.
			inheritsFrom:
				node.parent_id !== null ? nodeById(node.parent_id) : bookDefaults.value,
			seasonSource:
				node.season_source_node_id !== null
					? nodeById(node.season_source_node_id)
					: null,
			children: childrenOf(node.hierarchy_node_id),
			assets: assetsOf(node),
		};
	});

	/**
	 * True for the selected node itself and every one of its descendants —
	 * used by the hierarchy sidebar to dim everything outside the selected
	 * subtree. Nothing is dimmed when nothing is selected.
	 */
	const isInSelectedSubtree = (hierarchyNodeId: number): boolean => {
		if (selectedNodeId.value === null) return true;
		if (hierarchyNodeId === selectedNodeId.value) return true;

		return ancestorsOf(hierarchyNodeId).some(
			(ancestor) => ancestor.hierarchy_node_id === selectedNodeId.value
		);
	};

	/**
	 * The resolver's own figure when it's in, since that reflects real
	 * split/season/fill/manual-CPM resolution; falls back to a naive
	 * budget-percentage-only estimate (matches calc()'s reservation-first
	 * ordering) while the resolve request is still in flight.
	 */
	const bookBudget = (book: BasePriceBook): number => {
		const fromResolver = resolvedBook(book)?.budget;
		if (fromResolver !== undefined) return fromResolver;

		if (!plan.value) return 0;

		if (book.type === "reservation") {
			return !book.is_enabled
				? 0
				: (plan.value.target * (book.budget_percentage ?? 0)) / 100;
		}

		const reserved = reservationBooks.value
			.filter((b) => b.is_enabled)
			.reduce((sum, b) => sum + (b.budget_percentage ?? 0), 0);

		return plan.value.target * (1 - reserved / 100);
	};

	/**
	 * Gross impressions are entirely the resolver's domain — there is no
	 * sensible fallback estimate for them (unlike budget, which is derivable
	 * from budget_percentage alone), so this is null until resolved data is
	 * in. Backed by a deterministic stub (StaticLeafImpressionsProvider)
	 * until the real hierarchy/capacity resolution is built.
	 */
	const bookImpressions = (book: BasePriceBook): number | null =>
		resolvedBook(book)?.gross_impressions ?? null;

	const expectedRevenue = computed(
		() => resolved.value?.revenue ?? plan.value?.target ?? 0
	);
	const blendedCpm = computed<number | null>(
		() => resolved.value?.blended_cpm ?? null
	);
	/** Every screen's gross for the year — the inventory, not what the books add up to. */
	const grossImpressions = computed<number | null>(
		() => resolved.value?.gross_impressions ?? null
	);

	const varianceToTarget = computed<{ label: string; color: string }>(() => {
		if (!resolved.value || !resolved.value.target) {
			return { label: "—", color: "default" };
		}

		const diffPercentage =
			((resolved.value.revenue - resolved.value.target) /
				resolved.value.target) *
			100;

		if (Math.abs(diffPercentage) < 0.5) {
			return { label: "At budget", color: "success" };
		}

		return {
			label: `${diffPercentage > 0 ? "+" : ""}${diffPercentage.toFixed(1)}%`,
			color: diffPercentage < 0 ? "warning" : "info",
		};
	});

	/**
	 * A stable string for everything a save would write. Keys are sorted and
	 * identities left out, so it describes what the plan *is* rather than the
	 * order edits happened to arrive in or the uuid an override was handed
	 * when it was created here rather than loaded.
	 */
	const stable = (value: unknown): string =>
		JSON.stringify(value, (key, inner) => {
			if (key === "uuid" || key === "plan_uuid") return undefined;
			if (!inner || typeof inner !== "object" || Array.isArray(inner)) {
				return inner;
			}

			return Object.fromEntries(
				Object.entries(inner as Record<string, unknown>).sort(([a], [b]) =>
					a < b ? -1 : 1
				)
			);
		});

	const draftFingerprint = computed(() =>
		stable({
			target: plan.value?.target ?? null,
			// Sorted, because both collections get rebuilt wholesale — a bulk
			// override write and a book edit both replace the array — and a
			// reordering is not a change anyone made.
			books: draftBooks.value.map(stable).sort(),
			overrides: draftOverrides.value.map(stable).sort(),
		})
	);

	/** The fingerprint as last persisted — set on load, and again after a save reloads. */
	const savedFingerprint = ref("");

	/**
	 * Presents what is loaded as unsaved work, whatever it actually is.
	 *
	 * Used when an archived version is reopened: its contents are a perfectly
	 * good saved plan, but not the live one, so continuing from it is an edit
	 * to the chain and has to read as one — otherwise the editor would offer no
	 * Save and let you walk away thinking the old version was current again.
	 */
	function markUnsaved() {
		savedFingerprint.value = "";
	}

	/**
	 * Whether the working draft has moved since the version on screen was
	 * loaded. Nothing here is written until Save version, so this is what
	 * closing the editor would throw away.
	 */
	const hasUnsavedChanges = computed(
		() => plan.value !== null && draftFingerprint.value !== savedFingerprint.value
	);

	async function loadPlan(uuid: string) {
		loading.value = true;

		const response = await useApiClient("basePrice").getPlan(uuid);
		if (response.isOk()) {
			plan.value = response.value;
			// Drafts first: `books` reads draftBooks, so openInventoryBook is
			// empty until it is populated and the selection would land on null.
			draftBooks.value = committedBooks().map((book) => ({ ...book }));
			draftOverrides.value = plan.value.overrides
				? Object.values(plan.value.overrides)
				: [];
			selectedBookUuid.value = openInventoryBook.value?.uuid ?? null;
			savedFingerprint.value = draftFingerprint.value;
		}

		loading.value = false;

		// Not awaited: it aggregates a year of the source, and the editor is
		// usable long before that answer arrives.
		loadImpressionsStatus();
	}

	/**
	 * Whether the impressions this version prices against have gone stale.
	 * Only asked for the live version: a version from the history is meant to
	 * show the figures it was saved with, so staleness is not news there.
	 */
	const impressionsStatus = ref<BasePriceImpressionsStatusDTO | null>(null);

	async function loadImpressionsStatus() {
		impressionsStatus.value = null;

		if (plan.value?.status !== "open") return;

		const uuid = plan.value.uuid;
		const response = await useApiClient("basePrice").getImpressionsStatus(
			uuid
		);

		// Another version may have been opened while this one was computing.
		if (response.isOk() && plan.value?.uuid === uuid) {
			impressionsStatus.value = response.value;
		}
	}

	const impressionsChanged = computed(
		() => plan.value?.status === "open" && !!impressionsStatus.value?.changed
	);

	/**
	 * Always sends the *whole* draft (current target + every override),
	 * never just whatever field changed — a target edit and a pending
	 * override edit can be in flight at once, and neither should silently
	 * discard the other. See BasePriceController::resolve().
	 */
	async function loadResolvedPlan(uuid: string) {
		resolving.value = true;

		const response = await useApiClient("basePrice").resolvePlan(uuid, {
			target: plan.value?.target,
			books: draftBooks.value,
			overrides: draftOverrides.value,
		});
		if (response.isOk()) {
			resolved.value = response.value;

			if (selectedNodeId.value === null) {
				const root = selectedBookNodes.value.find(
					(node) => node.parent_id === null
				);
				selectedNodeId.value = root?.hierarchy_node_id ?? null;
			}
		}

		resolving.value = false;
	}

	// Debounced so a run of keystrokes doesn't fire a resolve per character —
	// matches the original design's "recompute on every change" model
	// without hammering the backend while typing. Reads plan/draftOverrides
	// at fire time (not passed as args), so it always sends the latest state
	// regardless of what triggered it.
	const debouncedResolvePreview = debounce((uuid: string) => {
		loadResolvedPlan(uuid);
	}, 400);

	function selectBook(uuid: string) {
		selectedBookUuid.value = uuid;
		selectedAssetId.value = null;
	}

	function selectNode(id: number) {
		selectedNodeId.value = id;
		selectedAssetId.value = null;
	}

	/**
	 * Selects one screen of `nodeId`. Sets the node first, because
	 * selectNode() clears any screen — a screen is only ever meaningful
	 * inside the node that carries it. Passing a null assetId selects the
	 * plain node.
	 */
	function selectAsset(nodeId: number, assetId: number | null) {
		selectNode(nodeId);
		selectedAssetId.value = assetId;
	}

	/**
	 * The draft row of `type` for whatever the editor is pointed at, or null
	 * when that target has none of its own.
	 *
	 * This — not the resolved payload — is what an editor tab seeds from. The
	 * resolver returns the *effect* of a configuration (a shape with any month
	 * pins already folded into it), so seeding from that would quietly rewrite
	 * the configuration every time you navigated away and back: pinned euros
	 * would come back as the percentages they happened to resolve to. The
	 * draft row is the thing that was actually applied, so restoring from it
	 * gives back exactly what was left behind.
	 */
	function targetOverride(
		type: BasePriceOverrideType
	): BasePriceOverride | null {
		const book = selectedBook.value;
		if (!book) return null;

		const forAsset = selectedAssetId.value !== null;

		return (
			draftOverrides.value.find(
				(override) =>
					override.book_uuid === book.uuid &&
					override.type === type &&
					(forAsset
						? override.asset_id === selectedAssetId.value
						: (override.hierarchy_node_id ?? null) === selectedNodeId.value &&
							(override.asset_id ?? null) === null)
			) ?? null
		);
	}

	/**
	 * The row of `type` in effect for whatever the editor is pointed at: its
	 * own, otherwise the nearest ancestor's in the same book — a screen first
	 * follows the node carrying it. Null when nothing up the chain sets one.
	 *
	 * The resolver does this walk for every type whose effect it reports, but
	 * locked days change no monthly figure, so it never resolves them; the
	 * Seasonality calendar walks the draft itself instead.
	 *
	 * `skipOwn` answers what the target would inherit with no row of its own.
	 * `covers` narrows the walk to rows that decide something in particular —
	 * locked days inherit month by month, so the row in effect for December is
	 * the nearest one that lists December, not merely the nearest one.
	 */
	const effectiveTargetOverride = (
		type: BasePriceOverrideType,
		{
			skipOwn = false,
			covers = () => true,
		}: {
			skipOwn?: boolean;
			covers?: (override: BasePriceOverride) => boolean;
		} = {}
	): BasePriceOverride | null => {
		const book = selectedBook.value;
		if (!book) return null;

		const rowFor = (nodeId: number | null, assetId: number | null) =>
			draftOverrides.value.find(
				(override) =>
					override.book_uuid === book.uuid &&
					override.type === type &&
					(assetId !== null
						? override.asset_id === assetId
						: (override.hierarchy_node_id ?? null) === nodeId &&
							(override.asset_id ?? null) === null) &&
					covers(override)
			) ?? null;

		if (selectedAssetId.value !== null && !skipOwn) {
			const own = rowFor(null, selectedAssetId.value);
			if (own) return own;
		}

		let node = selectedNode.value;

		// For a node, its own row is the first one the walk meets.
		if (node && skipOwn && selectedAssetId.value === null) {
			node = node.parent_id !== null ? nodeById(node.parent_id) : null;
		}

		while (node) {
			const row = rowFor(node.hierarchy_node_id, null);
			if (row) return row;

			node = node.parent_id !== null ? nodeById(node.parent_id) : null;
		}

		return null;
	};

	/**
	 * Every screen under whatever the editor is pointed at — just itself for a
	 * screen, and for a node its own screens plus every descendant's. What the
	 * daily impressions are summed over.
	 */
	const editTargetAssetIds = computed<number[]>(() => {
		if (selectedAssetId.value !== null) return [selectedAssetId.value];

		const root = selectedNode.value;
		if (!root) return [];

		const childrenByParent = new Map<number, ResolvedNodeDTO[]>();
		selectedBookNodes.value.forEach((node) => {
			if (node.parent_id === null) return;

			const siblings = childrenByParent.get(node.parent_id) ?? [];
			siblings.push(node);
			childrenByParent.set(node.parent_id, siblings);
		});

		const ids: number[] = [];
		const stack = [root];

		while (stack.length) {
			const node = stack.pop() as ResolvedNodeDTO;
			assetsOf(node).forEach((asset) => ids.push(asset.asset_id));
			stack.push(...(childrenByParent.get(node.hierarchy_node_id) ?? []));
		}

		return ids;
	});

	/**
	 * One month of the edit target's impressions, day by day, cached per plan
	 * version and target: they are the figures the version was saved with, so
	 * they cannot change while it is open, and stepping between months and
	 * targets should not refetch what is already here.
	 *
	 * @param month 1-12
	 */
	const dailyImpressionsCache = new Map<string, BasePriceDailyImpressionsDTO>();

	const fetchDailyImpressions = async (
		month: number
	): Promise<Result<BasePriceDailyImpressionsDTO, ApiError> | null> => {
		const target = editTarget.value;
		if (!plan.value || !target) return null;

		const key = `${plan.value.uuid}|${target.kind}:${target.id}|${month}`;
		const cached = dailyImpressionsCache.get(key);
		if (cached) return ok<BasePriceDailyImpressionsDTO, ApiError>(cached);

		const response = await useApiClient("basePrice").getDailyImpressions(
			plan.value.uuid,
			month,
			editTargetAssetIds.value
		);

		if (response.isOk()) dailyImpressionsCache.set(key, response.value);

		return response;
	};

	/**
	 * What the edit target's year would average with some of its own rows
	 * changed — resolved on the server against a copy of the draft, so nothing
	 * on screen moves. "Define average CPM" calibrates with it: the target's
	 * own figures are not the whole story when something below it decides its
	 * own fill or price, and only the resolver knows how those add up.
	 *
	 * Null when the resolve fails or the target is not in the answer.
	 */
	const previewTargetCpm = async (
		changes: Partial<Record<BasePriceOverrideType, Partial<BasePriceOverride>>>
	): Promise<number | null> => {
		const book = selectedBook.value;
		if (!plan.value || !book) return null;

		const assetId = selectedAssetId.value;
		const nodeId = selectedNodeId.value;

		const isTargets = (override: BasePriceOverride, type: string) =>
			override.book_uuid === book.uuid &&
			override.type === type &&
			(assetId !== null
				? override.asset_id === assetId
				: (override.hierarchy_node_id ?? null) === nodeId &&
					(override.asset_id ?? null) === null);

		const overrides = draftOverrides.value.map((override) => ({ ...override }));

		(Object.entries(changes) as [BasePriceOverrideType, Partial<BasePriceOverride>][]).forEach(
			([type, data]) => {
				const at = overrides.findIndex((override) => isTargets(override, type));

				if (at >= 0) {
					overrides[at] = { ...overrides[at], ...data };

					return;
				}

				overrides.push({
					uuid: randomUuid(),
					plan_uuid: plan.value!.uuid,
					book_uuid: book.uuid,
					hierarchy_node_id: assetId !== null ? null : nodeId,
					...(assetId !== null ? { asset_id: assetId } : {}),
					type,
					...data,
				} as BasePriceOverride);
			}
		);

		const response = await useApiClient("basePrice").resolvePlan(plan.value.uuid, {
			target: plan.value.target,
			books: draftBooks.value,
			overrides,
		});
		if (!response.isOk()) return null;

		const resolvedForBook = (Object.values(response.value.books) as ResolvedBookDTO[]).find(
			(candidate) => candidate.book_uuid === book.uuid
		);
		const node = (
			resolvedForBook ? (Object.values(resolvedForBook.nodes) as ResolvedNodeDTO[]) : []
		).find((candidate) => candidate.hierarchy_node_id === nodeId);
		if (!node) return null;

		if (assetId === null) return node.cpm;

		return assetsOf(node).find((asset) => asset.asset_id === assetId)?.cpm ?? null;
	};

	/**
	 * Where the edit target sits in the hierarchy, counted from the bottom —
	 * the deepest screen anywhere is 0, the root the highest. See
	 * hierarchyLevels(). Null with nothing selected.
	 */
	const editTargetLevel = computed<number | null>(() => {
		const node = selectedNode.value;
		if (!node) return null;

		const levels = hierarchyLevels(
			selectedBookNodes.value.map((candidate) => ({
				id: candidate.hierarchy_node_id,
				parentId: candidate.parent_id,
				hasAssets: assetsOf(candidate).length > 0,
			}))
		);

		return selectedAssetId.value !== null
			? levels.assetLevel(node.hierarchy_node_id)
			: levels.nodeLevel(node.hierarchy_node_id);
	});

	/**
	 * Whatever the editor is pointed at, the way the previous-years endpoints
	 * name it — its label and its level in the hierarchy — or null when there
	 * is nothing to ask about.
	 */
	const previousYearsRequest = () => {
		const target = editTarget.value;
		if (!plan.value || !target || editTargetLevel.value === null) return null;

		return {
			uuid: plan.value.uuid,
			body: { label: target.name, level: editTargetLevel.value },
		};
	};

	/** Earlier years' revenue for the edit target — what "Derive distribution from previous years" starts from. */
	const fetchPreviousYearsDistribution = async (): Promise<Result<
		Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearDistributionDTO,
		ApiError
	> | null> => {
		const request = previousYearsRequest();
		if (!request) return null;

		return useApiClient("basePrice").getPreviousYearsDistribution(request.uuid, request.body);
	};

	/** Earlier years' fill rate per month (0-100) for the edit target — what "Derive fill rate from previous years" starts from. */
	const fetchPreviousYearsFill = async (): Promise<Result<
		Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearAverageDTO,
		ApiError
	> | null> => {
		const request = previousYearsRequest();
		if (!request) return null;

		return useApiClient("basePrice").getPreviousYearsFill(request.uuid, request.body);
	};

	/** Earlier years' average CPM per month for the edit target — what "Derive CPM from previous years" starts from. */
	const fetchPreviousYearsCpm = async (): Promise<Result<
		Modules.BasePrice.Data.PreviousYear.BasePricePreviousYearAverageDTO,
		ApiError
	> | null> => {
		const request = previousYearsRequest();
		if (!request) return null;

		return useApiClient("basePrice").getPreviousYearsCpm(request.uuid, request.body);
	};

	/**
	 * Writes one override against whatever the editor is currently pointed
	 * at — the node, or the screen drilled into it. This is what the
	 * Seasonality and Fill & CPM tabs call, so neither has to know which
	 * kind of target it is editing or which column the row lands in.
	 */
	function setTargetOverride(
		type: BasePriceOverrideType,
		data: Partial<BasePriceOverride> | null
	) {
		const book = selectedBook.value;
		if (!book) return;

		if (selectedAssetId.value !== null) {
			setDraftOverride(book.uuid, null, type, data, selectedAssetId.value);
			return;
		}

		if (selectedNodeId.value !== null) {
			setDraftOverride(book.uuid, selectedNodeId.value, type, data);
		}
	}

	/**
	 * Not persisted yet (no PATCH/save endpoint exists) — but the resolved
	 * stats (blended CPM, expected revenue, variance, every book/node figure)
	 * DO update live, since this previews the new target against the
	 * resolver, debounced so a run of keystrokes doesn't fire a request per
	 * character.
	 */
	function setTarget(target: number) {
		if (!plan.value) return;

		plan.value.target = target;
		debouncedResolvePreview(plan.value.uuid);
	}

	/**
	 * Sets (or clears, when `data` is null) one override in the draft —
	 * the general-purpose mechanism every override-editing component should
	 * call. Not persisted; triggers the same debounced preview as setTarget.
	 *
	 * A row targets *either* `hierarchyNodeId` (every existing type) *or*
	 * `assetId` (asset_fixed_budget only) — never both, matching
	 * BasePriceOverrideDTO's own "exactly one of the two" shape. Pass
	 * `null` for `hierarchyNodeId` when setting an asset-level override
	 * (see setAssetFixedBudget()); every other caller keeps passing a real
	 * node id and omitting `assetId` entirely, unchanged.
	 */
	function setDraftOverride(
		bookUuid: string,
		hierarchyNodeId: number | null,
		type: BasePriceOverrideType,
		data: Partial<BasePriceOverride> | null,
		assetId?: number
	) {
		if (!plan.value) return;

		const index = draftOverrides.value.findIndex(
			(override) =>
				override.book_uuid === bookUuid &&
				override.type === type &&
				(override.hierarchy_node_id ?? null) === hierarchyNodeId &&
				(override.asset_id ?? null) === (assetId ?? null)
		);

		if (data === null) {
			if (index >= 0) draftOverrides.value.splice(index, 1);
		} else if (index >= 0) {
			draftOverrides.value[index] = { ...draftOverrides.value[index], ...data };
		} else {
			draftOverrides.value.push({
				uuid: randomUuid(),
				plan_uuid: plan.value.uuid,
				book_uuid: bookUuid,
				hierarchy_node_id: hierarchyNodeId,
				type,
				...(assetId !== undefined ? { asset_id: assetId } : {}),
				...data,
			} as BasePriceOverride);
		}

		debouncedResolvePreview(plan.value.uuid);
	}

	/**
	 * Writes many overrides at once, for a bulk action over the whole
	 * hierarchy. Not a loop over setDraftOverride(): that scans the draft
	 * array for every row it writes, so N writes cost N² comparisons across
	 * reactive proxies, and each one schedules its own resolve. On a portfolio
	 * with a few thousand screens that was enough to take the tab down.
	 *
	 * Here the lookup is a Map built once, the array is rebuilt as a plain
	 * value and assigned back in a single mutation, and exactly one resolve is
	 * scheduled at the end.
	 */
	function setDraftOverrides(
		entries: {
			bookUuid: string;
			hierarchyNodeId: number | null;
			type: BasePriceOverrideType;
			data: Partial<BasePriceOverride>;
			assetId?: number;
		}[]
	) {
		if (!plan.value || !entries.length) return;

		const keyOf = (
			bookUuid: string,
			type: string,
			nodeId: number | null,
			assetId: number | null
		) => `${bookUuid}|${type}|${nodeId ?? ""}|${assetId ?? ""}`;

		const next = [...draftOverrides.value];
		const index = new Map<string, number>();

		next.forEach((override, at) =>
			index.set(
				keyOf(
					override.book_uuid,
					override.type,
					override.hierarchy_node_id ?? null,
					override.asset_id ?? null
				),
				at
			)
		);

		entries.forEach((entry) => {
			const assetId = entry.assetId ?? null;
			const key = keyOf(
				entry.bookUuid,
				entry.type,
				entry.hierarchyNodeId,
				assetId
			);
			const at = index.get(key);

			if (at === undefined) {
				index.set(key, next.length);
				next.push({
					uuid: randomUuid(),
					plan_uuid: plan.value!.uuid,
					book_uuid: entry.bookUuid,
					hierarchy_node_id: entry.hierarchyNodeId,
					type: entry.type,
					...(assetId !== null ? { asset_id: assetId } : {}),
					...entry.data,
				} as BasePriceOverride);

				return;
			}

			next[at] = { ...next[at], ...entry.data };
		});

		draftOverrides.value = next;
		debouncedResolvePreview(plan.value.uuid);
	}

	/**
	 * Convenience wrapper over setDraftOverride for the budget-split tab's %
	 * mode. Mutually exclusive with a fixed-budget override on the same
	 * node — setting one always clears the other, so a node is never left
	 * carrying both at once.
	 */
	function setSplitShare(
		bookUuid: string,
		hierarchyNodeId: number,
		percentage: number
	) {
		setDraftOverride(bookUuid, hierarchyNodeId, "fixed_budget", null);
		setDraftOverride(bookUuid, hierarchyNodeId, "split", { percentage });
	}

	/**
	 * Convenience wrapper over setDraftOverride for the budget-split tab's €
	 * mode. The stored value is the fixed euro amount itself, never a
	 * percentage — see BasePriceBookResolver::budgetForChild(), which reads
	 * it fresh on every resolve so it never drifts if the parent's own
	 * budget later changes. Mutually exclusive with a split override on the
	 * same node.
	 */
	function setFixedBudget(
		bookUuid: string,
		hierarchyNodeId: number,
		amount: number
	) {
		setDraftOverride(bookUuid, hierarchyNodeId, "split", null);
		setDraftOverride(bookUuid, hierarchyNodeId, "fixed_budget", { amount });
	}

	/**
	 * Sets (or clears, when `amount` is null) a fixed-€ override for one
	 * specific screen — the screen-level equivalent of setFixedBudget(). A
	 * screen has no % equivalent (no SplitOverrideDTO counterpart), so
	 * unlike setSplitShare()/setFixedBudget() there's nothing else to
	 * mutually clear here. Targets `asset_id`, not a hierarchy node — see
	 * AssetFixedBudgetOverrideDTO — so `hierarchyNodeId` is always null.
	 */
	function setAssetFixedBudget(
		bookUuid: string,
		assetId: number,
		amount: number | null
	) {
		setDraftOverride(
			bookUuid,
			null,
			"asset_fixed_budget",
			amount === null ? null : { asset_id: assetId, amount },
			assetId
		);
	}

	/**
	 * Replaces the whole book set — what the Budget books dialog commits on
	 * Save. Any override belonging to a book that is no longer there goes with
	 * it, so a deleted book cannot leave rows behind pointing at nothing.
	 */
	function setDraftBooks(next: BasePriceBook[]) {
		if (!plan.value) return;

		const uuids = new Set(next.map((book) => book.uuid));

		draftBooks.value = next;
		draftOverrides.value = draftOverrides.value.filter((override) =>
			uuids.has(override.book_uuid)
		);

		if (selectedBookUuid.value && !uuids.has(selectedBookUuid.value)) {
			selectedBookUuid.value = openInventoryBook.value?.uuid ?? null;
		}

		debouncedResolvePreview(plan.value.uuid);
	}

	const saving = ref(false);

	/**
	 * Persists the working draft as a new plan. Nothing is ever edited in
	 * place, so this returns the plan that was created — a different uuid to
	 * the one being edited — and the caller has to navigate to it, since the
	 * route carries the uuid.
	 *
	 * Gated on hasAllocationErrors: a plan that promises away more than it has
	 * is a configuration nobody should be able to publish, and every one of
	 * those conditions is listed in the Issues dialog with a way to reach it.
	 */
	async function savePlan(): Promise<BasePricePlanDTO | null> {
		if (!plan.value || hasAllocationErrors.value) return null;

		saving.value = true;

		const response = await useApiClient("basePrice").savePlan(plan.value.uuid, {
			target: plan.value.target,
			books: draftBooks.value,
			overrides: draftOverrides.value,
		});

		saving.value = false;

		return response.isOk() ? response.value : null;
	}

	const refreshingImpressions = ref(false);

	/**
	 * Takes the impressions afresh and saves the working draft as a new plan
	 * priced against them. Like savePlan(), the caller has to navigate to the
	 * plan that comes back. Null when there is nothing that may be saved.
	 */
	async function refreshImpressions(): Promise<Result<
		BasePricePlanDTO,
		ApiError
	> | null> {
		if (!plan.value || hasAllocationErrors.value) return null;

		refreshingImpressions.value = true;

		const response = await useApiClient("basePrice").refreshImpressions(
			plan.value.uuid,
			{
				target: plan.value.target,
				books: draftBooks.value,
				overrides: draftOverrides.value,
			}
		);

		refreshingImpressions.value = false;

		return response;
	}

	/**
	 * Every version of the plan being edited, newest first — the chain it
	 * belongs to, archived versions included. Loaded on demand rather than with
	 * the plan: only the versions drawer reads it, and it changes only when a
	 * save adds to the chain.
	 */
	const versions = ref<BasePricePlanMinimalDTO[]>([]);
	const loadingVersions = ref(false);

	async function loadVersions() {
		if (!plan.value) return;

		loadingVersions.value = true;

		const response = await useApiClient("basePrice").getPlanVersions(
			plan.value.uuid
		);
		if (response.isOk()) {
			versions.value = response.value;
		}

		loadingVersions.value = false;
	}

	function $reset() {
		dailyImpressionsCache.clear();
		impressionsStatus.value = null;
		versions.value = [];
		loadingVersions.value = false;
		plan.value = null;
		resolved.value = null;
		draftBooks.value = [];
		draftOverrides.value = [];
		savedFingerprint.value = "";
		selectedBookUuid.value = null;
		selectedNodeId.value = null;
		selectedAssetId.value = null;
		activeDetailTab.value = "split";
		loading.value = false;
		resolving.value = false;
	}

	return {
		plan,
		loading,
		resolving,
		saving,
		hasUnsavedChanges,
		markUnsaved,
		versions,
		loadingVersions,
		loadVersions,
		resolved,
		draftOverrides,
		draftBooks,
		books,
		resolvedBook,
		hasAllocationErrors,
		openInventoryBook,
		reservationBooks,
		selectedBookUuid,
		activeDetailTab,
		selectedBook,
		selectedNodeId,
		selectedAssetId,
		selectedBookNodes,
		selectedNode,
		selectedAsset,
		assetsOf,
		editTarget,
		planIssues,
		overReservedAssetIds,
		nodeOverReserved,
		shareBudgetIssue,
		targetOverride,
		effectiveTargetOverride,
		previewTargetCpm,
		fetchPreviousYearsDistribution,
		fetchPreviousYearsCpm,
		fetchPreviousYearsFill,
		editTargetLevel,
		editTargetAssetIds,
		fetchDailyImpressions,
		nodeById,
		childrenOf,
		ancestorsOf,
		nodeKind,
		isInSelectedSubtree,
		bookBudget,
		bookImpressions,
		expectedRevenue,
		blendedCpm,
		grossImpressions,
		varianceToTarget,
		loadPlan,
		loadResolvedPlan,
		savePlan,
		refreshingImpressions,
		refreshImpressions,
		impressionsStatus,
		impressionsChanged,
		selectBook,
		selectNode,
		selectAsset,
		setDraftBooks,
		setTargetOverride,
		setTarget,
		setDraftOverride,
		setDraftOverrides,
		setSplitShare,
		setFixedBudget,
		setAssetFixedBudget,
		$reset,
	};
});
